import { createId } from "../../app/runtimeContext";
import type { CyanIntelligenceRepository } from "./repositories";
import { TaskPlanner, type TaskPlannerInput } from "./taskPlanner";
import type { ExecutionPlan, Goal, Objective } from "./types";

export type PlanningInput = {
  goal: Goal;
  objectiveTitles: string[];
};

export class PlanningEngine {
  constructor(
    private readonly repository: CyanIntelligenceRepository,
    private readonly taskPlanner = new TaskPlanner(),
  ) {}

  async createPlan(input: PlanningInput): Promise<ExecutionPlan> {
    const objectives: Objective[] = input.objectiveTitles.map(
      (title, index) => {
        const taskInput: TaskPlannerInput = {
          title: `Execute ${title}`,
          description: `Complete objective: ${title}`,
          priority: index === 0 ? "high" : "medium",
          risk: index === 0 ? 0.25 : 0.15,
          estimatedEffortHours: 4 + index,
          expectedOutcome: `Objective delivered: ${title}`,
          dependencies: index === 0 ? [] : [`objective_${index - 1}`],
          subtasks: [
            {
              title: `Validate ${title}`,
              description: `Validate outcome for ${title}`,
              priority: "medium",
              risk: 0.1,
              estimatedEffortHours: 1,
              expectedOutcome: "Validation complete",
            },
          ],
        };
        return {
          id: `objective_${index}`,
          title,
          expectedOutcome: `Outcome for ${title}`,
          status: "planned",
          priority: taskInput.priority,
          tasks: [this.taskPlanner.createTask(taskInput)],
        };
      },
    );

    const plan: ExecutionPlan = {
      id: createId("execution_plan"),
      goalId: input.goal.id,
      organizationId: input.goal.organizationId,
      productId: input.goal.productId,
      summary: `Hierarchical plan for ${input.goal.title}`,
      objectives,
      status: "planned",
      totalEstimatedEffortHours: objectives
        .flatMap((objective) => objective.tasks)
        .reduce(
          (sum, task) =>
            sum +
            task.estimatedEffortHours +
            task.subtasks.reduce(
              (subsum, subtask) => subsum + subtask.estimatedEffortHours,
              0,
            ),
          0,
        ),
      createdAt: new Date().toISOString(),
    };
    await this.repository.savePlan(plan);
    return plan;
  }
}
